import { Fragment, useEffect, useState, type ReactNode } from "react"
import {
  Activity,
  BookUser,
  Building2,
  Cctv,
  Cpu,
  FileSignature,
  Globe,
  LayoutDashboard,
  LogOut,
  MessageCircle,
  MessageSquare,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  Receipt,
  Route,
  ScrollText,
  ShieldCheck,
  Sun,
  Users,
  Zap,
  type LucideIcon,
} from "lucide-react"

import type { Permission } from "@/config/permissions"
import { useDialogs, type DialogNames } from "@/hooks/use-dialogs"
import { useTheme } from "@/hooks/use-theme"
import { cn } from "@/lib/utils"
import { useAuth } from "@/store/auth"
import { OncologyNavSection } from "@/features/oncology/components/oncology-nav-section"

import { Authorized } from "../auth/authorized"
import { Button } from "../ui/button"
import { Separator } from "../ui/separator"
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip"
import ActiveLink from "./active-link"
import Dialogs from "./dialogs"

// Types
export interface NavigationItem {
  to: string
  icon: LucideIcon
  label: string
  permission?: Permission
  hideInProduction?: boolean
  badge?: {
    label: string
    variant: "count" | "new" | "alert"
    /** Most-urgent open tier, drives colour for the "alert" variant (1 = red). */
    tier?: number
  }
}

interface NavigationSection {
  title: string
  items: NavigationItem[]
  permissions?: Permission[]
}

// Navigation Configuration
const NAVIGATION_CONFIG: {
  dashboard: NavigationItem
  sections: NavigationSection[]
} = {
  dashboard: {
    to: "/dashboard",
    icon: LayoutDashboard,
    label: "Dashboard",
  },
  sections: [
    {
      title: "Manage",
      permissions: [
        "view_facility",
        "view_pathway",
        "view_pathwaytriggerrule",
        "view_patient",
        "view_user",
        "view_device",
      ],
      items: [
        {
          to: "/manage/facilities",
          icon: Building2,
          label: "Facilities",
          permission: "view_facility",
        },
        {
          to: "/manage/pathways",
          icon: Route,
          label: "Pathways",
          permission: "view_pathway",
        },
        {
          to: "/manage/adt-trigger-rules",
          icon: Zap,
          label: "Transition Care",
          permission: "view_pathwaytriggerrule",
          hideInProduction: true,
        },
        {
          to: "/query-analytics",
          icon: MessageSquare,
          label: "Nexus Hub",
          permission: "view_patient",
          hideInProduction: true,
        },
        {
          to: "/manage/users",
          icon: Users,
          label: "Staff",
          permission: "view_user",
        },
        {
          to: "/manage/patients",
          icon: BookUser,
          label: "Patients",
          permission: "view_patient",
          // badge: { label: "71", variant: "count" },
        },
        {
          to: "/manage/communication",
          icon: MessageCircle,
          label: "Communication",
          permission: "view_patient",
          // badge: { label: "5", variant: "count" },
        },
        // Patient Watchlist: not yet a real route in the app — enable once built.
        // {
        //   to: "/manage/patient-watchlist",
        //   icon: UserPlus,
        //   label: "Patient Watchlist",
        // },
        {
          to: "/manage/devices",
          icon: Cctv,
          label: "Devices",
          permission: "view_device",
        },
        // Referrals: hidden from nav, route/component still exist.
        // {
        //   to: "/manage/referrals",
        //   icon: Share2,
        //   label: "Referrals",
        //   permission: "view_referral",
        // },
      ],
    },
    {
      title: "Reports",
      permissions: ["view_patient", "view_patientbillableduration"],
      items: [
        {
          to: "/reports/biometrics",
          icon: Activity,
          label: "Biometrics",
          permission: "view_patient",
        },
        {
          to: "/reports/billing",
          icon: Receipt,
          label: "Billing",
          permission: "view_patientbillableduration",
        },
        {
          to: "/reports/compliance",
          icon: ShieldCheck,
          label: "Compliance",
          permission: "view_patient",
        },
        {
          to: "/reports/consent",
          icon: FileSignature,
          label: "Consent",
          permission: "view_patient",
        },
        {
          to: "/reports/demographics",
          icon: Globe,
          label: "Demographics",
          permission: "view_patient",
        },
      ],
    },
    {
      title: "System",
      permissions: ["view_logentry", "view_ai_roadmap"],
      items: [
        {
          to: "/system/audit-logs",
          icon: ScrollText,
          label: "Audit Logs",
          permission: "view_logentry",
        },
        {
          to: "/system/ai-roadmap",
          icon: Cpu,
          label: "AI Roadmap",
          permission: "view_ai_roadmap",
          badge: { label: "New", variant: "new" },
        },
      ],
    },
  ],
}

// Colour an alert badge by its most-urgent open tier (tier 1 = red).
function alertTierClasses(tier?: number) {
  switch (tier) {
    case 1:
      return "bg-red-600 text-white"
    case 2:
      return "bg-amber-500 text-white"
    case 3:
      return "bg-yellow-400 text-navy-900"
    default:
      return "bg-red-600 text-white"
  }
}

// Components
function NavigationBadge({ badge }: { badge: NavigationItem["badge"] }) {
  if (!badge) return null

  if (badge.variant === "new") {
    return (
      <span className="shrink-0 rounded-full bg-teal px-2 py-0.5 text-xs font-bold text-white">
        {badge.label}
      </span>
    )
  }

  if (badge.variant === "alert") {
    return (
      <span
        className={cn(
          "shrink-0 rounded-full px-2 py-0.5 text-xs font-bold tabular-nums",
          alertTierClasses(badge.tier)
        )}
      >
        {badge.label}
      </span>
    )
  }

  return (
    <span className="shrink-0 rounded-full bg-foreground/10 px-2.5 py-0.5 text-xs font-medium text-navy-600 dark:text-navy-600/80">
      {badge.label}
    </span>
  )
}

export function NavigationLink({
  item,
  isCollapsed,
}: {
  item: NavigationItem
  isCollapsed: boolean
}) {
  const Icon = item.icon

  const linkContent = (
    <div className="group" role="menuitem" aria-label={item.label}>
      <ActiveLink
        to={item.to}
        activeClassName="relative rounded-full bg-gradient-to-r from-teal/20 to-teal/5 text-foreground hover:from-teal/20 hover:to-teal/5 hover:text-foreground"
        inactiveClassName="rounded-full text-navy-600 hover:bg-teal/5 hover:text-navy-600 dark:text-navy-600/80 dark:hover:text-navy-600/80"
      >
        {({ isActive }: { isActive: boolean }) => (
          <>
            {isActive && (
              <svg
                className="absolute left-0 top-0 h-10 w-5 text-teal-700"
                viewBox="0 0 20 40"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M20 1C9 1 1 9.5 1 20C1 30.5 9 39 20 39"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            )}
            <Icon
              className={cn(
                "size-4 shrink-0 transition-transform duration-200 group-hover:scale-110",
                !isCollapsed && "mr-3",
                isActive && "text-teal-700 dark:text-teal"
              )}
            />
            {!isCollapsed && (
              <>
                <span className="flex-1 text-left">{item.label}</span>
                <NavigationBadge badge={item.badge} />
              </>
            )}
          </>
        )}
      </ActiveLink>
    </div>
  )

  if (isCollapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
        <TooltipContent side="right">
          <p>{item.label}</p>
        </TooltipContent>
      </Tooltip>
    )
  }

  return linkContent
}

export function SectionHeader({
  title,
  isCollapsed,
  action,
}: {
  title: string
  isCollapsed: boolean
  /** Optional trailing element (e.g. a "setup" chip), hidden when collapsed. */
  action?: ReactNode
}) {
  if (isCollapsed) {
    return <Separator className="my-3" />
  }

  return (
    <div className="mb-2 mt-5 flex items-center justify-between px-3">
      <h2 className="text-sm font-bold uppercase tracking-widest text-foreground">
        {title}
      </h2>
      {action}
    </div>
  )
}

function SectionAuthorization({
  permissions,
  children,
}: {
  permissions?: Permission[]
  children: ReactNode
}) {
  if (!permissions) return children

  return (
    <Authorized permissions={permissions} ifAll={false}>
      {children}
    </Authorized>
  )
}

export function Sidebar() {
  const [isCollapsed, setIsCollapsed] = useState(false)
  const { theme, setTheme } = useTheme()
  const isDay = theme !== "dark"
  const dialogs = useDialogs<DialogNames<typeof Dialogs>>()
  // Specialty-gated: the Oncology section only shows for users whose login
  // payload grants the ONCOLOGY feature (facility specialty).
  const user = useAuth(s => s.user)
  const hasOncology = user?.features?.includes("ONCOLOGY") ?? false

  // Load collapsed state from localStorage on mount
  useEffect(() => {
    const savedState = localStorage.getItem("sidebar-collapsed")
    if (savedState !== null) {
      setIsCollapsed(savedState === "true")
    }
  }, [])

  // Save collapsed state to localStorage when it changes
  const toggleCollapse = () => {
    setIsCollapsed(prev => {
      const newState = !prev
      localStorage.setItem("sidebar-collapsed", String(newState))
      return newState
    })
  }

  return (
    <div
      className={cn(
        "flex flex-col border-r bg-sidebar py-4 transition-all duration-300",
        isCollapsed ? "w-16 min-w-16 px-2" : "w-64 min-w-64 px-4"
      )}
      role="navigation"
      aria-label="Main navigation"
    >
      {/* Logo and Toggle */}
      <div
        className={cn(
          "flex items-center pb-4",
          isCollapsed ? "flex-col gap-2" : "justify-between"
        )}
      >
        {!isCollapsed && (
          <div className="flex w-full justify-center">
            <img
              src="/light-logo.png"
              alt="Health Logo"
              className="hidden h-12 w-32 transition-all duration-200 hover:scale-105 dark:hidden lg:block"
            />
            <img
              src="/dark-logo.png"
              alt="Health Logo"
              className="hidden h-12 w-32 transition-all duration-200 hover:scale-105 dark:lg:block"
            />
          </div>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleCollapse}
          className={cn(
            "h-8 w-8 shrink-0 transition-all",
            isCollapsed && "mx-auto"
          )}
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {isCollapsed ? (
            <PanelLeftOpen className="size-4" />
          ) : (
            <PanelLeftClose className="size-4" />
          )}
        </Button>
      </div>

      <Separator className="mb-4" />

      <div className="flex flex-1 flex-col overflow-y-auto">
        {/* Dashboard Link */}
        <div className="flex flex-col gap-y-1" role="menu">
          <NavigationLink
            item={NAVIGATION_CONFIG.dashboard}
            isCollapsed={isCollapsed}
          />
        </div>

        {/* Oncology monitoring — pinned to the top for specialty-enabled users.
            Its own component because it needs server data for the live
            open-alert badge. */}
        {hasOncology && <OncologyNavSection isCollapsed={isCollapsed} />}

        {/* Navigation Sections */}
        {NAVIGATION_CONFIG.sections.map(section => (
          <Fragment key={section.title}>
            <SectionAuthorization permissions={section.permissions}>
              <div>
                <SectionHeader title={section.title} isCollapsed={isCollapsed} />
                <div className="flex flex-col gap-y-1" role="menu">
                  {section.items.filter(item => !(item.hideInProduction && ["production", "prod"].includes(import.meta.env.VITE_APP_ENV ?? ""))).map(item => {
                    const linkElement = (
                      <NavigationLink item={item} isCollapsed={isCollapsed} />
                    )

                    if (item.permission) {
                      return (
                        <Authorized key={item.to} permissions={[item.permission]}>
                          {linkElement}
                        </Authorized>
                      )
                    }

                    return <div key={item.to}>{linkElement}</div>
                  })}
                </div>
              </div>
            </SectionAuthorization>
          </Fragment>
        ))}
      </div>

      {/* Day/Night + Logout */}
      <div className="mt-2 flex flex-col gap-3 border-t pt-4">
        {!isCollapsed && (
          <div className="flex h-10 items-center rounded-full bg-foreground/5 p-1">
            <button
              type="button"
              onClick={() => setTheme("light")}
              className={cn(
                "flex h-full flex-1 items-center justify-center gap-1.5 rounded-full text-xs font-bold transition-colors",
                isDay
                  ? "bg-teal text-white"
                  : "text-navy-600 dark:text-navy-600/80"
              )}
            >
              <Sun className="size-3.5" />
              Day
            </button>
            <button
              type="button"
              onClick={() => setTheme("dark")}
              className={cn(
                "flex h-full flex-1 items-center justify-center gap-1.5 rounded-full text-xs font-bold transition-colors",
                !isDay
                  ? "bg-teal text-white"
                  : "text-navy-600 dark:text-navy-600/80"
              )}
            >
              <Moon className="size-3.5" />
              Night
            </button>
          </div>
        )}
        <Button
          variant="ghost"
          onClick={dialogs.toggle("SignOutConfirmation")}
          className={cn(
            "h-10 justify-start gap-2 rounded-full text-navy-600 hover:bg-teal/5 hover:text-navy-600 dark:text-navy-600/80 dark:hover:text-navy-600/80",
            isCollapsed && "justify-center"
          )}
          aria-label="Logout"
        >
          <LogOut className="size-4 shrink-0" />
          {!isCollapsed && "Logout"}
        </Button>
      </div>
      <Dialogs.SignOutConfirmation
        {...dialogs.getProps("SignOutConfirmation")}
      />
    </div>
  )
}
